// =============================================================================
// VINUM - Toast Component
// Migrated from Flutter CherryToast with EXACT styling
// =============================================================================

import React from 'react';
import { cn } from '@/lib/cn';
import { useUIStore, selectToasts } from '@vinum/shared';
import type { ToastType } from '@vinum/shared';

// =============================================================================
// Single Toast Item
// =============================================================================

interface ToastItemProps {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  onClose: () => void;
}

const ToastItem: React.FC<ToastItemProps> = ({ type, title, message, onClose }) => {
  const iconStyles = {
    success: 'text-success',
    error: 'text-error',
    warning: 'text-warning',
    info: 'text-info',
  };

  const bgStyles = {
    success: 'bg-success/10 border-success/20',
    error: 'bg-error/10 border-error/20',
    warning: 'bg-warning/10 border-warning/20',
    info: 'bg-info/10 border-info/20',
  };

  const icons = {
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  return (
    <div
      className={cn(
        'flex items-start gap-3 p-4 rounded-lg border shadow-lg backdrop-blur-sm',
        'animate-slide-in-right',
        'bg-white dark:bg-gray-800',
        bgStyles[type]
      )}
    >
      <div className={cn('flex-shrink-0', iconStyles[type])}>{icons[type]}</div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
        {message && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{message}</p>
        )}
      </div>

      <button
        onClick={onClose}
        className="flex-shrink-0 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

// =============================================================================
// Toast Container
// =============================================================================

export const ToastContainer: React.FC = () => {
  const toasts = useUIStore(selectToasts);
  const removeToast = useUIStore((state) => state.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:bottom-4 sm:left-auto sm:right-4 z-[9998] flex flex-col gap-2 sm:max-w-sm sm:w-full">
      {toasts.map((toast) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

// =============================================================================
// useToast hook for imperative usage
// STABILIZED: Returns memoized object to prevent render loops
// =============================================================================

export function useToast() {
  // Select individual stable functions from store (not entire store object)
  const showSuccessToast = useUIStore((state) => state.showSuccessToast);
  const showErrorToast = useUIStore((state) => state.showErrorToast);
  const showWarningToast = useUIStore((state) => state.showWarningToast);
  const showInfoToast = useUIStore((state) => state.showInfoToast);

  // Memoize the returned object to maintain stable reference
  return React.useMemo(
    () => ({
      success: showSuccessToast,
      error: showErrorToast,
      warning: showWarningToast,
      info: showInfoToast,
    }),
    [showSuccessToast, showErrorToast, showWarningToast, showInfoToast]
  );
}
